import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faVideo, faGlobe, faPodcast, faMobileScreen, faMusic } from '@fortawesome/free-solid-svg-icons';
import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
import './Resources.css';

// Create Sanity client directly in component to avoid Next.js conflicts
const sanityClient = createClient({
  projectId: process.env.REACT_APP_SANITY_PROJECT_ID || process.env.NEXT_PUBLIC_SANITY_PROJECT_ID || 'd8gvm4xl',
  dataset: process.env.REACT_APP_SANITY_DATASET || process.env.NEXT_PUBLIC_SANITY_DATASET || 'production',
  useCdn: false, // Disable CDN to get fresh data
  apiVersion: '2023-01-01',
  token: process.env.SANITY_API_TOKEN // Add token for fresh data
});

const builder = imageUrlBuilder(sanityClient);
const urlFor = (source) => builder.image(source);

const ResourcesSanity = () => {


  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Icon mapping for sections
  const sectionIcons = {
    'books': faBook,
    'music': faMusic,
    'videos': faVideo,
    'websites': faGlobe,
    'podcasts': faPodcast,
    'apps': faMobileScreen
  };

  useEffect(() => {
    const loadResources = async () => {
      try {

        
        // Fetch sections with their related cards (filtered by pageType)
        const query = `
          *[_type == "section" && pageType == "resources"] | order(order asc) {
            _id,
            title,
            slug,
            order,
            "cards": *[_type == "card" && references(^._id) && pageType == "resources"] | order(order asc) {
              _id,
              title,
              href,
              body,
              image,
              order
            }
          }
        `;
        

        
        // Add cache-busting parameter to force fresh data
        const data = await sanityClient.fetch(query, {}, {
          cache: 'no-store'
        });
        

        setSections(data);
      } catch (err) {

        setError('Failed to load resources');
        console.error('Error loading resources:', err);
      } finally {
        setLoading(false);
      }
    };

    loadResources();
  }, []);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const limitCards = (cards, isExpanded) => {
    if (isExpanded || isMobile) return cards;
    return cards.slice(0, 8);
  };

  const getSectionIcon = (title) => {
    const key = title.toLowerCase();
    return sectionIcons[key] || faBook;
  };



  if (loading) {
    return (
      <Container>
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  return (
    <>
      {sections.map((section, index) => (
        <section 
          key={section._id} 
          className={`section-padding resource-section ${index % 2 === 0 ? 'card-bg' : 'main-bg'}`}
        >
          <Container>
            {index === 0 && (
              <div className="text-center mb-4">
                <h1 className="section-title">Resources</h1>
              </div>
            )}

            <div className="mb-4">
              <div className="d-flex align-items-center mb-4">
                <FontAwesomeIcon 
                  icon={getSectionIcon(section.title)} 
                  size="lg" 
                  className="me-2 text-primary" 
                />
                <h2 className="mb-0 fs-4">{section.title}</h2>
                <span className="mx-2 subtitle-text small mb-0">|</span>
                <p className="subtitle-text mb-0 small">
                  {section.cards.length} item{section.cards.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <Row className="book-gallery-small justify-content-start g-3">
              {limitCards(section.cards, expandedSections[section._id]).map((card) => (
                <Col key={card._id} xs={6} sm={6} md={3} lg={3} className="mb-3">
                  <div className="book-card-small">
                    <a href={card.href} target="_blank" rel="noopener noreferrer">
                      {card.image ? (
                        <img 
                          src={urlFor(card.image).width(300).height(400).fit('crop').url()}
                          alt={card.title}
                          className="book-image"
                        />
                      ) : (
                        <div className="placeholder-media">
                          <FontAwesomeIcon 
                            icon={getSectionIcon(section.title)} 
                            className="placeholder-icon" 
                          />
                        </div>
                      )}
                      <div className="book-title small text-center"> 
                        {card.title.includes('(') && card.title.includes(')') ? (
                          <>
                            <div>{card.title.substring(0, card.title.indexOf('(')).trim()}</div>
                            <div>({card.title.substring(card.title.indexOf('(') + 1, card.title.indexOf(')')).trim()})</div>
                          </>
                        ) : (
                          card.title
                        )}
                      </div>
                      {card.body && (
                        <p className="website-description small">{card.body}</p>
                      )}
                    </a>
                  </div>
                </Col>
              ))}
            </Row>
            
            {section.cards.length > 8 && !isMobile && (
              <div className="text-center mt-4">
                <Button 
                  variant="outline-primary" 
                  onClick={() => toggleSection(section._id)}
                  className="more-btn"
                >
                  {expandedSections[section._id] ? 'Show Less' : `Show All ${section.cards.length} Items`}
                </Button>
              </div>
            )}
          </Container>
        </section>
      ))}

      <div className="text-center mt-5 py-4" data-source="sanity">
        <small className="text-muted">Content managed via Sanity CMS</small>
      </div>
    </>
  );
};

export default ResourcesSanity;